import React, { useState } from "react";
import Modal from "react-modal";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import { useUpdateEffect } from "react-use";
import { useRecoilState, useResetRecoilState } from "recoil";
import { getQuizForApply, responseQuiz } from "../../api";
import Error from "../../components/common/Error";
import {
  Alert,
  Button,
  ErrorAlert,
  SaveButton,
} from "../../components/common/Forms";
import Icon from "../../components/common/Icon";
import Quiz from "../../components/quiz/Quiz";
import {
  quizInfoContents,
  quizInfoTitle,
  quizList,
  quizResponseList,
  quizResponseTime,
} from "../../states/atoms/quizState";
import { shuffle, useLocationQuery } from "../../util/common";
// import { useTitle } from "react-use";

const QuizForPage = () => {
  const params = useParams();
  const query = useLocationQuery();
  const navigate = useNavigate();

  const { qid } = params;
  const { target } = query;
  const [title, setTitle] = useRecoilState(quizInfoTitle);
  const [contents, setContents] = useRecoilState(quizInfoContents);
  const [list, setList] = useRecoilState(quizList);
  const [responses, setResposeList] = useRecoilState(quizResponseList);
  const [time, setTime] = useRecoilState(quizResponseTime);
  const resetList = useResetRecoilState(quizList);
  const resetResponse = useResetRecoilState(quizResponseList);
  const resetTime = useResetRecoilState(quizResponseTime);
  const [error, setError] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [modal, setModal] = useState({
    isOpen: false,
    loading: false,
    result: null,
    error: false,
  });
  const [btnDisabled, setBtnStatus] = useState(true);
  const [readOnly, setReadOnly] = useState(false);
  const [lecture_id, setLectureId] = useState(null);
  const queryClient = useQueryClient();
  const {
    status,
    data,
    error: err,
  } = useQuery({
    queryKey: ["quiz", qid],
    queryFn: () => getQuizForApply({ qid, student_id: target }),
  });

  const quizMttn = useMutation({
    mutationFn: responseQuiz,
    onSettled: async (data, error) => {
      setModal({ ...modal, error: false });
      if (data.result) {
        resetList();
        resetResponse();
        setModal({
          isOpen: true,
          loading: false,
          result: "퀴즈 응답이 완료되었습니다.",
          error: false,
        });
      } else {
        setReadOnly(false);
        setModal({
          isOpen: true,
          loading: false,
          result: data.error,
          error: true,
        });
      }
      queryClient.invalidateQueries({ queryKey: ["quiz"] });
    },
  });

  useUpdateEffect(() => {
    if (status === "success" && data.result) {
      setError(false);
      setLectureId(data.data.lecture_id);
      setTitle(data.data.title);
      setContents(data.data.contents);
      const tmpList = data.data.questions.reduce((acc, cur, i) => {
        if (cur.type === "text" || cur.type === "textarea") {
          acc.push({
            q: cur,
            option: data.data.options[i],
            answers: data.data.answers[i],
          });
        } else {
          acc.push({
            q: cur,
            option: { content: shuffle(data.data.options[i].content) },
            answers: data.data.answers[i],
          });
        }
        return acc;
      }, []);
      setList(tmpList);
      setResposeList(new Array(tmpList.length).fill(null));
    } else {
      setLoadError(data.error);
    }
  }, [status]);

  useUpdateEffect(() => {
    setError(err || false);
  }, [err]);

  useUpdateEffect(() => {
    const v = responses.every((item) => !!item);
    setBtnStatus(!v);
  }, [responses]);

  const onChange = (e) => {
    if (!time) {
      setTime(new Date().valueOf());
    }
    const { type, name, id, value } = e.target;
    const newRes = [...responses];
    let n = -1;
    let v = null;
    if (type === "radio") {
      n = name.split("_")[1];
      v = id.split("-")[2];
    } else if (type === "textarea") {
      n = name.split("_")[1];
      v = value;
    } else if (type === "checkbox") {
      n = name.split("_")[1];
      if (newRes[n - 1]) {
        let temp = [...newRes[n - 1]];
        let tempV = id.split("-")[2];
        if (temp.includes(tempV)) {
          temp = temp.filter((val) => val !== tempV);
        } else {
          temp.push(tempV);
        }
        v = temp;
      } else {
        v = [id.split("-")[2]];
      }
    }
    newRes.splice(n - 1, 1, v);
    setResposeList(newRes);
  };

  const handleSave = () => {
    setReadOnly(true);
    const requiredTime = new Date().valueOf() - time;
    quizMttn.mutate({
      qid,
      student_id: target,
      lecture_id,
      responses,
      requiredTime,
    });
    resetTime();
  };

  const handleClose = () => {
    setModal({
      isOpen: false,
      loading: false,
      result: null,
      error: false,
    });
    if (!modal.error) {
      navigate(
        `/quiz/result?lecture_id=${lecture_id}&student_id=${target}&quiz_id=${qid}`
      );
    }
  };

  return (
    <>
      {loadError && <Error>{loadError}</Error>}
      {!loadError && (
        <div className="container mx-auto lg:my-8 lg:max-w-screen-lg">
          <div className="lg:rounded-t-3xl bg-cyan-500 px-6 py-4 md:px-10 md:py-8">
            <h1 className="break-keep md:text-5xl text-3xl leading-tight text-white font-bold">
              {title}
            </h1>
            <h3 className="mt-6 break-keep text-white">
              {contents.map((v, i) => (
                <p key={`content_${qid}_${i}`}>{v}</p>
              ))}
            </h3>
          </div>
          <hr />
          <div className="md:rounded-b-3xl border-t border-zinc-200 bg-white md:p-8 shadow flex flex-col gap-6">
            {error && <ErrorAlert>{error}</ErrorAlert>}
            {list.length > 0 &&
              list.map(({ q, option }, i) => {
                return (
                  <div key={`q_${i}`}>
                    <Quiz
                      {...{
                        n: i + 1,
                        option:
                          q.type === "text" || q.type === "textarea"
                            ? option.content
                            : "",
                        options:
                          q.type === "text" || q.type === "textarea"
                            ? [{ content: "" }]
                            : option.content,
                        q: q.content,
                        readOnly,
                        response: responses[i],
                        type: q.type,
                        onChange,
                      }}
                    />
                  </div>
                );
              })}
            <hr />
            <div className="w-full px-4 pb-4 ml-auto">
              <SaveButton onClick={handleSave} disabled={btnDisabled}>
                제출
              </SaveButton>
            </div>
          </div>
          <Modal
            className="h-full text-center flex"
            isOpen={modal.isOpen}
            ariaHideApp={false}
          >
            <div className="card w-1/2 bg-white shadow-xl m-auto">
              <div className="card-body overflow-hidden gap-4">
                {!modal.loading && modal.result && !modal.error && (
                  <Alert>{modal.result}</Alert>
                )}
                {modal.loading && (
                  <progress className="progress progress-primary w-full"></progress>
                )}
                {modal.error && <ErrorAlert>{modal.result}</ErrorAlert>}
              </div>
              <div className="card-actions justify-center">
                {modal.error && (
                  <Button
                    type="primary"
                    className="rounded-t-none"
                    onClick={handleClose}
                    block
                  >
                    <Icon name="x-mark" size="sm" className="inline-block" />
                    닫기
                  </Button>
                )}
                {!modal.error && (
                  <Button
                    type="primary"
                    className="rounded-t-none"
                    onClick={handleClose}
                    block
                  >
                    <Icon name="check" size="sm" className="inline-block" />
                    결과 확인
                  </Button>
                )}
              </div>
            </div>
          </Modal>
        </div>
      )}
    </>
  );
};

export default QuizForPage;
